import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ChevronLeft, 
  ChevronRight, 
  Home,
  X,
  List,
  CheckCircle,
  Maximize,
  Minimize,
  Clock,
  StickyNote,
  Globe,
  Target,
  Lightbulb,
  TrendingUp,
  Users,
  BookOpen,
  Award,
  DollarSign,
  Building2,
  ArrowRight,
  Sparkles,
  GraduationCap,
  BarChart3,
  FileText,
  Rocket
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PresentationPage() {
  const { courseId, scheduledId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [slides, setSlides] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showOutline, setShowOutline] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    fetchCourse();
  }, [courseId, scheduledId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses/${courseId}`);
      setCourse(response.data);
      
      const originalSlides = response.data.slides || [];
      
      // If we have a scheduledId, fetch sponsors and insert sponsor slides
      if (scheduledId) {
        try {
          const sponsorsRes = await axios.get(`${API_URL}/public/scheduled-courses/${scheduledId}/sponsors`);
          setSponsors(sponsorsRes.data);
          
          // Insert sponsor slides at strategic positions
          const slidesWithSponsors = insertSponsorSlides(originalSlides, sponsorsRes.data);
          setSlides(slidesWithSponsors);
        } catch (err) {
          // No sponsors or error - use original slides
          setSlides(originalSlides);
        }
      } else {
        setSlides(originalSlides);
      }
      
      // Start presentation session
      const sessionRes = await axios.post(`${API_URL}/presentations/start?course_id=${courseId}&delivery_format=webinar`);
      setSessionId(sessionRes.data.id);
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Insert sponsor slides at strategic intervals
  const insertSponsorSlides = (originalSlides, sponsorData) => {
    if (!sponsorData || sponsorData.length === 0) return originalSlides;
    
    const result = [...originalSlides];
    const totalSlides = originalSlides.length;
    
    // Calculate insertion points based on slot positions
    // Slot 1: After intro (~10% into presentation)
    // Slot 2: Mid-course (~40%)
    // Slot 3: After key content (~70%)
    // Slot 4: Before closing (~90%)
    const insertionPoints = [
      Math.floor(totalSlides * 0.10),
      Math.floor(totalSlides * 0.40),
      Math.floor(totalSlides * 0.70),
      Math.floor(totalSlides * 0.90)
    ];
    
    // Sort sponsors by slot position and insert in reverse order (to maintain correct indices)
    const sortedSponsors = [...sponsorData].sort((a, b) => b.slot_position - a.slot_position);
    
    sortedSponsors.forEach(sponsor => {
      const slotIndex = sponsor.slot_position - 1; // 0-indexed
      if (slotIndex >= 0 && slotIndex < insertionPoints.length) {
        const insertAt = insertionPoints[slotIndex];
        const sponsorSlide = {
          id: `sponsor-${sponsor.slot_position}`,
          title: 'Thank You to Our Sponsor',
          content: sponsor.company_name,
          slide_type: 'sponsor',
          sponsor_data: sponsor,
          notes: `Acknowledge sponsor: ${sponsor.company_name}`
        };
        result.splice(insertAt, 0, sponsorSlide);
      }
    });
    
    return result;
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setShowOutline(false);
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
      e.preventDefault();
      nextSlide();
    } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
      e.preventDefault();
      prevSlide();
    } else if (e.key === 'Escape') {
      if (isFullscreen) {
        document.exitFullscreen();
      } else {
        setShowOutline(false);
        setShowNotes(false);
      }
    } else if (e.key === 'f' || e.key === 'F') {
      toggleFullscreen();
    } else if (e.key === 'n' || e.key === 'N') {
      setShowNotes(!showNotes);
    }
  }, [currentSlide, slides.length, isFullscreen, showNotes]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const endPresentation = async () => {
    if (sessionId) {
      try {
        await axios.put(`${API_URL}/presentations/${sessionId}/end`);
      } catch (error) {
        console.error('Failed to end session');
      }
    }
    navigate('/dashboard');
  };

  const progressPercent = slides.length > 0 ? Math.round(((currentSlide + 1) / slides.length) * 100) : 0;

  // Helper function to get section icons based on slide title
  const getSectionIcon = (title) => {
    const titleLower = (title || '').toLowerCase();
    const iconClass = "w-8 h-8 text-white/80";
    
    if (titleLower.includes('case study') || titleLower.includes('example')) {
      return <FileText className={iconClass} />;
    } else if (titleLower.includes('overview') || titleLower.includes('introduction') || titleLower.includes('intro')) {
      return <BookOpen className={iconClass} />;
    } else if (titleLower.includes('strategy') || titleLower.includes('strategies')) {
      return <Target className={iconClass} />;
    } else if (titleLower.includes('financing') || titleLower.includes('money') || titleLower.includes('loan') || titleLower.includes('cost')) {
      return <DollarSign className={iconClass} />;
    } else if (titleLower.includes('analysis') || titleLower.includes('numbers') || titleLower.includes('roi')) {
      return <BarChart3 className={iconClass} />;
    } else if (titleLower.includes('risk') || titleLower.includes('management')) {
      return <TrendingUp className={iconClass} />;
    } else if (titleLower.includes('building') || titleLower.includes('construction') || titleLower.includes('property')) {
      return <Building2 className={iconClass} />;
    } else if (titleLower.includes('team') || titleLower.includes('client') || titleLower.includes('network')) {
      return <Users className={iconClass} />;
    } else if (titleLower.includes('action') || titleLower.includes('next step') || titleLower.includes('implement')) {
      return <Rocket className={iconClass} />;
    } else if (titleLower.includes('takeaway') || titleLower.includes('key') || titleLower.includes('summary')) {
      return <Award className={iconClass} />;
    } else {
      return <Lightbulb className={iconClass} />;
    }
  };

  // Helper function to get content icons
  const getContentIcon = (title, color) => {
    const titleLower = (title || '').toLowerCase();
    const iconStyle = { color: color || '#1E3A5F' };
    const iconClass = "w-6 h-6";
    
    if (titleLower.includes('what is') || titleLower.includes('defined') || titleLower.includes('definition')) {
      return <BookOpen className={iconClass} style={iconStyle} />;
    } else if (titleLower.includes('why') || titleLower.includes('benefit') || titleLower.includes('advantage')) {
      return <Lightbulb className={iconClass} style={iconStyle} />;
    } else if (titleLower.includes('how') || titleLower.includes('step') || titleLower.includes('process')) {
      return <ArrowRight className={iconClass} style={iconStyle} />;
    } else if (titleLower.includes('number') || titleLower.includes('cost') || titleLower.includes('price') || titleLower.includes('profit') || titleLower.includes('budget')) {
      return <DollarSign className={iconClass} style={iconStyle} />;
    } else if (titleLower.includes('result') || titleLower.includes('outcome') || titleLower.includes('return')) {
      return <TrendingUp className={iconClass} style={iconStyle} />;
    } else if (titleLower.includes('client') || titleLower.includes('investor') || titleLower.includes('builder') || titleLower.includes('agent')) {
      return <Users className={iconClass} style={iconStyle} />;
    } else if (titleLower.includes('property') || titleLower.includes('building') || titleLower.includes('construction') || titleLower.includes('renovation')) {
      return <Building2 className={iconClass} style={iconStyle} />;
    } else if (titleLower.includes('risk') || titleLower.includes('flag') || titleLower.includes('warning')) {
      return <Target className={iconClass} style={iconStyle} />;
    } else if (titleLower.includes('success') || titleLower.includes('key') || titleLower.includes('takeaway')) {
      return <Award className={iconClass} style={iconStyle} />;
    } else if (titleLower.includes('action') || titleLower.includes('next') || titleLower.includes('implement')) {
      return <Rocket className={iconClass} style={iconStyle} />;
    } else if (titleLower.includes('thank')) {
      return <Sparkles className={iconClass} style={iconStyle} />;
    } else {
      return <CheckCircle className={iconClass} style={iconStyle} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white animate-pulse">Loading presentation...</div>
      </div>
    );
  }

  const slide = slides[currentSlide];
  const isTitle = slide?.slide_type === 'title' || slide?.slide_type === 'section';
  const isSponsor = slide?.slide_type === 'sponsor';

  return (
    <div className={`h-screen flex flex-col ${isFullscreen ? 'bg-black' : 'bg-slate-100'}`}>
      {/* Header - Hidden in fullscreen */}
      {!isFullscreen && (
        <header className="h-14 bg-[#1E3A5F] text-white flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={endPresentation}
              className="text-slate-300 hover:text-white hover:bg-[#162d4a]"
              data-testid="exit-presentation"
            >
              <Home className="w-4 h-4 mr-2" />
              Exit
            </Button>
            <div className="h-6 w-px bg-slate-600" />
            <div>
              <h1 className="text-sm font-medium">{course?.title}</h1>
              <p className="text-xs text-slate-400">{course?.credit_hours} Hour CE</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-slate-300 border-slate-500">
              <Clock className="w-3 h-3 mr-1" />
              {course?.credit_hours}hr
            </Badge>
            
            {sponsors.length > 0 && (
              <Badge variant="outline" className="text-emerald-300 border-emerald-500">
                {sponsors.length} Sponsor{sponsors.length > 1 ? 's' : ''}
              </Badge>
            )}
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowNotes(!showNotes)}
              className={`text-slate-300 hover:text-white ${showNotes ? 'bg-[#162d4a]' : ''}`}
              data-testid="toggle-notes"
            >
              <StickyNote className="w-4 h-4" />
            </Button>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowOutline(!showOutline)}
              className={`text-slate-300 hover:text-white ${showOutline ? 'bg-[#162d4a]' : ''}`}
              data-testid="toggle-outline"
            >
              <List className="w-4 h-4" />
            </Button>

            <Button 
              variant="ghost" 
              size="sm"
              onClick={toggleFullscreen}
              className="text-slate-300 hover:text-white"
              data-testid="toggle-fullscreen"
            >
              {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </Button>
          </div>
        </header>
      )}

      {/* Main Content */}
      <div className="flex-1 relative overflow-hidden flex">
        {/* Slide Content */}
        <div className="flex-1 flex items-center justify-center p-8">
          {isSponsor ? (
            /* Sponsor Slide */
            <div 
              className="w-full max-w-5xl rounded-xl shadow-2xl overflow-hidden bg-gradient-to-br from-[#1E3A5F] to-[#0c1e33] relative"
            >
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-white/5 rounded-full" />
              
              <div className="p-12 min-h-[500px] flex flex-col items-center justify-center text-center relative z-10">
                {/* Slide Number */}
                <div className="text-sm mb-4 text-sky-400">
                  {currentSlide + 1} / {slides.length}
                </div>
                
                {/* Thank You Text */}
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles className="w-5 h-5 text-sky-400" />
                  <p className="text-sky-300 text-lg">Thank You to Our Sponsor</p>
                  <Sparkles className="w-5 h-5 text-sky-400" />
                </div>
                
                {/* Sponsor Logo */}
                {slide?.sponsor_data?.logo_url ? (
                  <div className="bg-white rounded-2xl p-8 mb-6 shadow-lg transform hover:scale-105 transition-transform">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${slide.sponsor_data.logo_url}`}
                      alt={slide.sponsor_data.company_name}
                      className="max-w-md max-h-48 object-contain"
                    />
                  </div>
                ) : (
                  <div className="bg-white/10 rounded-2xl p-8 mb-6 backdrop-blur-sm border border-white/20">
                    <h2 
                      className="text-5xl font-bold text-white"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {slide?.sponsor_data?.company_name || slide?.content}
                    </h2>
                  </div>
                )}
                
                {/* Company Name (if logo exists) */}
                {slide?.sponsor_data?.logo_url && (
                  <h2 
                    className="text-3xl font-bold text-white mb-4"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {slide?.sponsor_data?.company_name}
                  </h2>
                )}
                
                {/* Website */}
                {slide?.sponsor_data?.website && (
                  <div className="flex items-center gap-2 text-sky-300">
                    <Globe className="w-5 h-5" />
                    <span className="text-lg">{slide.sponsor_data.website}</span>
                  </div>
                )}
              </div>
            </div>
          ) : isTitle ? (
            /* Title/Section Slide - Enhanced */
            <div 
              className="w-full max-w-5xl rounded-xl shadow-2xl overflow-hidden relative"
              style={{ backgroundColor: course?.color || '#1E3A5F' }}
            >
              {/* Decorative Background Elements */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
                <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-white/30 rounded-full" />
                <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white/20 rounded-full" />
                <div className="absolute bottom-1/4 left-1/4 w-2 h-2 bg-white/20 rounded-full" />
                {/* Grid Pattern */}
                <div className="absolute inset-0 opacity-5" style={{
                  backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                  backgroundSize: '30px 30px'
                }} />
              </div>

              <div className="p-12 min-h-[500px] flex flex-col justify-center relative z-10">
                {/* Slide Number Badge */}
                <div className="inline-flex items-center gap-2 text-sm mb-8 text-white/60 bg-white/10 px-3 py-1 rounded-full w-fit">
                  <FileText className="w-4 h-4" />
                  <span>{currentSlide + 1} / {slides.length}</span>
                </div>

                {/* Icon for Section Slides */}
                {slide?.slide_type === 'section' && (
                  <div className="mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-4 border border-white/20">
                      {getSectionIcon(slide?.title)}
                    </div>
                  </div>
                )}

                {/* Title */}
                <h1 
                  className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                  data-testid="slide-title"
                >
                  {slide?.title}
                </h1>

                {/* Subtitle/Content */}
                {slide?.content && (
                  <p className="text-xl md:text-2xl text-white/80 max-w-3xl leading-relaxed">
                    {slide?.content}
                  </p>
                )}

                {/* Decorative Line */}
                <div className="mt-8 w-24 h-1 bg-gradient-to-r from-white/60 to-transparent rounded-full" />
              </div>
            </div>
          ) : (
            /* Regular Content Slide - Enhanced */
            <div className="w-full max-w-5xl rounded-xl shadow-2xl overflow-hidden bg-white relative">
              {/* Decorative Corner Elements */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-slate-100 to-transparent" />
              <div className="absolute bottom-0 right-0 w-48 h-48 bg-gradient-to-tl from-slate-50 to-transparent" />
              
              {/* Accent Bar */}
              <div 
                className="absolute top-0 left-0 w-2 h-full"
                style={{ backgroundColor: course?.color || '#1E3A5F' }}
              />

              <div className="p-12 min-h-[500px] flex flex-col justify-center relative z-10 pl-16">
                {/* Slide Number & Progress */}
                <div className="flex items-center gap-4 mb-8">
                  <span className="text-sm text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                    {currentSlide + 1} / {slides.length}
                  </span>
                  <div className="flex-1 max-w-32 h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-300"
                      style={{ 
                        width: `${((currentSlide + 1) / slides.length) * 100}%`,
                        backgroundColor: course?.color || '#1E3A5F'
                      }}
                    />
                  </div>
                </div>

                {/* Title with Icon */}
                <div className="flex items-start gap-4 mb-6">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
                    style={{ backgroundColor: `${course?.color}15` || '#1E3A5F15' }}
                  >
                    {getContentIcon(slide?.title, course?.color)}
                  </div>
                  <h1 
                    className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                    data-testid="slide-title"
                  >
                    {slide?.title}
                  </h1>
                </div>

                {/* Content */}
                {slide?.slide_type === 'bullets' && slide?.bullets ? (
                  <ul className="space-y-4 mt-4 ml-16">
                    {slide.bullets.map((bullet, idx) => (
                      <li 
                        key={idx} 
                        className="flex items-start gap-4 text-lg text-slate-600 group"
                      >
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 transition-transform group-hover:scale-110"
                          style={{ backgroundColor: `${course?.color}15` || '#1E3A5F15' }}
                        >
                          <CheckCircle 
                            className="w-5 h-5" 
                            style={{ color: course?.color || '#1E3A5F' }}
                          />
                        </div>
                        <span className="leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p 
                    className="text-xl leading-relaxed text-slate-600 ml-16"
                    data-testid="slide-content"
                  >
                    {slide?.content}
                  </p>
                )}
              </div>

              {/* Bottom Decoration */}
              <div className="absolute bottom-6 right-8 flex items-center gap-2 text-slate-300">
                <GraduationCap className="w-5 h-5" />
                <span className="text-sm font-medium">{course?.title}</span>
              </div>
            </div>
          )}
        </div>

        {/* Speaker Notes Panel */}
        {showNotes && slide?.notes && !isFullscreen && (
          <div className="w-80 bg-amber-50 border-l border-amber-200 p-4 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3">
              <StickyNote className="w-4 h-4 text-amber-600" />
              <h3 className="font-medium text-amber-900">Speaker Notes</h3>
            </div>
            <p className="text-sm text-amber-800 leading-relaxed">{slide.notes}</p>
          </div>
        )}

        {/* Outline Sidebar */}
        {showOutline && !isFullscreen && (
          <div className="absolute inset-y-0 right-0 w-80 bg-white border-l border-slate-200 shadow-lg z-10 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <h3 className="font-semibold text-slate-900">Slide Outline</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowOutline(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {slides.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${
                    idx === currentSlide 
                      ? 'bg-blue-50 border-l-4 border-blue-600' 
                      : 'hover:bg-slate-50'
                  }`}
                  data-testid={`outline-slide-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${idx === currentSlide ? 'text-blue-600' : 'text-slate-400'}`}>
                      {idx + 1}
                    </span>
                    <span className={`text-sm truncate ${idx === currentSlide ? 'text-blue-900 font-medium' : 'text-slate-600'}`}>
                      {s.title}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-4 rounded-full bg-white/90 shadow-lg hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          data-testid="prev-slide-btn"
        >
          <ChevronLeft className="w-8 h-8 text-slate-700" />
        </button>
        
        <button
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-4 rounded-full bg-white/90 shadow-lg hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          data-testid="next-slide-btn"
        >
          <ChevronRight className="w-8 h-8 text-slate-700" />
        </button>
      </div>

      {/* Footer Controls - Hidden in fullscreen */}
      {!isFullscreen && (
        <footer className="h-16 bg-white border-t border-slate-200 flex items-center justify-between px-6 flex-shrink-0">
          <div className="text-sm text-slate-600">
            Slide {currentSlide + 1} of {slides.length}
          </div>

          <div className="flex-1 max-w-xl mx-8">
            <Progress value={progressPercent} className="h-2" />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 mr-4">
              Press <kbd className="px-2 py-1 bg-slate-100 rounded text-xs">F</kbd> for fullscreen
            </span>
            <Button 
              variant="outline" 
              onClick={prevSlide}
              disabled={currentSlide === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Prev
            </Button>
            <Button 
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
              className="bg-[#1E3A5F] hover:bg-[#162d4a] text-white"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </footer>
      )}

      {/* Fullscreen indicator */}
      {isFullscreen && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
          {currentSlide + 1} / {slides.length} • Press ESC to exit
        </div>
      )}
    </div>
  );
}
